import { useState, useEffect } from "react";
import { Expand, Package2, X, ZoomIn } from "lucide-react";

import type { ProductRecord } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// ----------------------------------------------------------------------------

interface ProductPreviewDialogProps {
  className?: string;
  isOpen: boolean;
  product: ProductRecord | null;
  colorBadges: string[];
  temperatureBadges: string[];
  onOpenChange: (open: boolean) => void;
  onAddToOrder: (product: ProductRecord) => void;
}

export function ProductPreviewDialog(props: ProductPreviewDialogProps) {
  // -------------------------------------
  // Props destructuring
  // -------------------------------------

  const {
    className,
    isOpen,
    product,
    colorBadges,
    temperatureBadges,
    onOpenChange,
    onAddToOrder,
  } = props;

  // -------------------------------------
  // Hooks (e.g. useState, useMemo ...)
  // -------------------------------------

  const [imageError, setImageError] = useState(false);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [selectedGalleryIndex, setSelectedGalleryIndex] = useState(0);

  // -------------------------------------
  // Effects
  // -------------------------------------

  useEffect(() => {
    setImageError(false);
    setIsFullscreenOpen(false);
    setSelectedGalleryIndex(0);
  }, [product]);

  useEffect(() => {
    if (!isOpen) {
      setIsFullscreenOpen(false);
    }
  }, [isOpen]);

  // -------------------------------------
  // Component local variables
  // -------------------------------------

  const badges = [
    ...colorBadges.map((label) => ({ label, tone: "gray" as const })),
    ...temperatureBadges.map((label) => ({ label, tone: "blue" as const })),
  ];

  // Simulated multi-image gallery derived from primary image URL or placeholders
  const galleryImages = product?.imageUrl ? [product.imageUrl] : [];
  const currentImageUrl =
    galleryImages[selectedGalleryIndex] || product?.imageUrl;

  const rawCategory = product?.categories?.[0] || "";
  const cleanCategory = rawCategory.includes(">")
    ? rawCategory
        .split(">")
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(-2, -1)[0] ||
      rawCategory.split(">").pop()?.trim() ||
      rawCategory
    : rawCategory;

  const descriptionText =
    product?.description && !product.description.includes(">")
      ? product.description
      : `Prodotto di illuminazione ${
          cleanCategory ? `categoria ${cleanCategory.toLowerCase()}` : "B2B"
        } Gea Luce con finitura di alta qualità e diffusore integrato.`;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={`sm:max-w-5xl p-0 overflow-hidden ${className || ""}`}
      >
        {product && (
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr]">
            {/* Left side: Image & Gallery */}
            <div className="bg-gray-50 p-6 sm:p-10 flex flex-col justify-between">
              <div className="relative aspect-[16/11] overflow-hidden rounded-3xl border border-gray-200 bg-white group flex items-center justify-center p-4">
                {product.imageUrl && !imageError ? (
                  <button
                    type="button"
                    className="relative h-full w-full overflow-hidden flex items-center justify-center cursor-zoom-in"
                    onClick={() => setIsFullscreenOpen(true)}
                  >
                    <img
                      src={currentImageUrl}
                      alt={product.name || product.code}
                      className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-[1.03]"
                      onError={() => setImageError(true)}
                    />
                    <div className="absolute inset-x-4 bottom-4 flex items-center justify-between rounded-2xl bg-black/60 px-4 py-3 text-sm text-white opacity-90 transition-opacity group-hover:opacity-100">
                      <span>Apri immagine a schermo intero</span>
                      <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1">
                        <ZoomIn className="h-4 w-4" />
                        Zoom
                      </span>
                    </div>
                    <div className="absolute top-4 right-4 rounded-xl bg-black/60 p-2 text-white opacity-80 transition-opacity group-hover:opacity-100">
                      <Expand className="h-4 w-4" />
                    </div>
                  </button>
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-gradient-to-br from-gray-100 to-gray-200 text-gray-500">
                    <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white shadow-sm">
                      <Package2 className="h-10 w-10 text-gray-400" />
                    </div>
                    <div className="text-center px-4">
                      <div className="text-sm font-semibold text-gray-700">
                        {product.name || product.code}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Immagine non disponibile dal feed B2B
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Gallery Thumbnails bar (only rendered if multiple images exist) */}
              {galleryImages.length > 1 && (
                <div className="mt-4 flex items-center justify-center gap-2">
                  {galleryImages.map((imgUrl, idx) => (
                    <button
                      key={`gallery-thumb-${idx}`}
                      onClick={() => setSelectedGalleryIndex(idx)}
                      className={`h-12 w-12 rounded-lg border overflow-hidden transition-all ${
                        selectedGalleryIndex === idx
                          ? "border-[#0D6EFD] ring-2 ring-blue-500/20"
                          : "border-gray-200 opacity-60 hover:opacity-100"
                      }`}
                    >
                      <img
                        src={imgUrl}
                        alt="thumb"
                        className="h-full w-full object-contain p-1"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right side: Product Metadata & Actions */}
            <div className="p-6 sm:p-10 flex flex-col justify-between">
              <div>
                <DialogHeader className="text-left">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    {product.brandName && (
                      <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                        {product.brandName}
                      </span>
                    )}
                    {cleanCategory && (
                      <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                        {cleanCategory}
                      </span>
                    )}
                  </div>
                  <DialogTitle className="text-2xl font-bold text-gray-900 leading-tight">
                    {product.name || product.code}
                  </DialogTitle>
                  <DialogDescription className="text-sm text-gray-500 mt-1">
                    SKU {product.sku || product.code} · Codice {product.code}
                  </DialogDescription>
                </DialogHeader>

                <div className="mt-6 space-y-6">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Prezzo unitario B2B
                    </div>
                    <div className="mt-1 text-3xl font-bold text-gray-900">
                      {product.price}
                    </div>
                  </div>

                  {badges.length > 0 && (
                    <div className="space-y-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Varianti disponibili
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {badges.map((badge) => (
                          <span
                            key={`${badge.tone}-${badge.label}`}
                            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
                              badge.tone === "blue"
                                ? "bg-blue-50 text-blue-700"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {badge.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Descrizione
                    </div>
                    <p className="text-sm leading-6 text-gray-600">
                      {descriptionText}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row pt-4 border-t border-gray-100">
                <Button
                  className="flex-1 bg-[#0D6EFD] hover:bg-blue-700 text-white"
                  onClick={() => onAddToOrder(product)}
                >
                  Aggiungi a nuovo ordine
                </Button>
                <Button
                  variant="outline"
                  className="border-gray-200 text-gray-700"
                  onClick={() => onOpenChange(false)}
                >
                  Chiudi
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>

      <Dialog open={isFullscreenOpen} onOpenChange={setIsFullscreenOpen}>
        <DialogContent
          fullscreen
          showCloseButton={false}
          className="w-screen h-screen rounded-none border-0 bg-black/95 p-0"
        >
          <div className="relative flex h-full w-full items-center justify-center overflow-hidden p-4 sm:p-6">
            <button
              type="button"
              aria-label="Chiudi immagine a schermo intero"
              className="absolute right-6 top-6 z-10 rounded-full bg-white/10 p-3 text-white transition-colors hover:bg-white/20"
              onClick={() => setIsFullscreenOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>

            {currentImageUrl && !imageError ? (
              <div className="flex h-full w-full items-center justify-center">
                <img
                  src={currentImageUrl}
                  alt={product?.name || product?.code || "Prodotto"}
                  className="block h-full w-full object-contain"
                  onError={() => setImageError(true)}
                />
              </div>
            ) : (
              <div className="flex h-full w-full max-w-3xl flex-col items-center justify-center gap-4 rounded-3xl border border-white/10 bg-white/5 text-white/80">
                <Package2 className="h-12 w-12" />
                <div className="text-lg font-medium">
                  Immagine non disponibile
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
